import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ListRecycleBinResult } from '@whinerp/client';
import {
  Button,
  ConfirmDialog,
  InlineAction,
  Num,
  Panel,
  SectionLabel,
  Skeleton,
} from '@whinerp/ui';
import { formatCount, formatDate, formatRelativeDate } from '@whinerp/contracts/format';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';

/**
 * The recycle bin (F-019).
 *
 * Delete and restore were built and the bin was not: nothing listed what was
 * in it, nothing removed anything permanently, and the tier-dependent window
 * was a sentence in the acceptance criteria. The bin only grew.
 *
 * Every row says when it goes for good, as a DATE. "Gone on Tuesday" is
 * something somebody can act on; "in 4 days" is a number they have to convert,
 * and it is wrong by one the moment they read it tomorrow.
 *
 * A purge asks first, and the confirmation says what survives: the audit trail
 * keeps a record that it happened and who did it. That is the difference
 * between a destructive action and an unaccountable one.
 */
type BinRow = ListRecycleBinResult['rows'][number];

export function RecycleBinPanel(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const errorMessage = useErrorMessage();
  const queryClient = useQueryClient();

  const [objectKey, setObjectKey] = useState<string>('');
  const [confirming, setConfirming] = useState<BinRow | null>(null);

  const bin = useQuery<ListRecycleBinResult, ApiError>({
    queryKey: ['recycle-bin', objectKey],
    queryFn: () => api.listRecycleBin({ query: objectKey ? { objectKey } : {} }),
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['recycle-bin'] });
  };

  const restore = useMutation({
    mutationFn: (row: BinRow) =>
      api.restoreRecord({ path: { objectKey: row.objectKey, recordId: row.recordId } }),
    onSuccess: refresh,
  });

  const purge = useMutation({
    mutationFn: (row: BinRow) =>
      api.purgeRecord({ path: { objectKey: row.objectKey, recordId: row.recordId } }),
    onSuccess: async () => {
      setConfirming(null);
      await refresh();
    },
  });

  const purgeAll = useMutation({
    mutationFn: () => api.purgeExpired({}),
    onSuccess: refresh,
  });

  if (bin.isPending) return <Skeleton className="h-block-md w-full" />;
  if (bin.isError) return <></>;

  const rows = bin.data.rows;
  /*
   * The kinds present, derived from what is in the bin.
   *
   * A filter listing every registered object would be fifteen options of
   * which thirteen say "nothing", which is a control that mostly answers no.
   */
  const kinds = [...new Map(rows.map((row) => [row.objectKey, row.objectLabel])).entries()];
  const expired = rows.filter((row) => row.expired).length;

  return (
    <Panel>
      <div data-component="RecycleBinPanel" className="flex flex-col gap-3">
        <SectionLabel>{t('bin.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">
          {t('bin.body', { values: { days: formatCount(bin.data.retentionDays, format) } })}
        </p>

        {rows.length === 0 ? (
          <p data-bin-empty className="text-dense text-ink-600">
            {t('bin.empty')}
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              {kinds.length > 1 ? (
                <label className="flex items-center gap-2 text-dense text-ink-600">
                  {t('bin.filter')}
                  <select
                    data-bin-filter
                    value={objectKey}
                    onChange={(event) => setObjectKey(event.currentTarget.value)}
                    className="h-input rounded-control border border-control bg-surface px-2 text-dense text-ink"
                  >
                    <option value="">{t('bin.all')}</option>
                    {kinds.map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              {/*
                * Only when something is actually past its window. A button
                * that would remove nothing is one somebody presses to find
                * out, and this is the one control in the product that
                * destroys data.
                */}
              {expired > 0 ? (
                <Button
                  intent="secondary"
                  size="small"
                  data-bin-purge-expired
                  busy={purgeAll.isPending}
                  onClick={() => purgeAll.mutate()}
                >
                  {t('bin.purgeExpired')}
                </Button>
              ) : null}
            </div>

            {/* Why anything past its window is still here. */}
            {expired > 0 ? (
              <p className="text-meta text-ink-meta">{t('bin.noScheduler')}</p>
            ) : null}

            <div data-scroll-container="x" className="overflow-x-auto">
              <table className="w-full text-dense">
                <thead>
                  <tr className="text-start">
                    <th scope="col" className="p-1 text-start text-micro uppercase tracking-label text-ink-label">
                      {t('bin.record')}
                    </th>
                    <th scope="col" className="p-1 text-start text-micro uppercase tracking-label text-ink-label">
                      {t('bin.deletedBy')}
                    </th>
                    <th scope="col" className="p-1 text-start text-micro uppercase tracking-label text-ink-label">
                      {t('bin.deletedAt')}
                    </th>
                    <th scope="col" className="p-1 text-start text-micro uppercase tracking-label text-ink-label">
                      {t('bin.expires')}
                    </th>
                    <th scope="col" className="p-1" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.recordId} data-bin-row={row.recordId} className="border-t border-t-rule">
                      <td className="p-1 text-ink">
                        <span className="flex flex-col">
                          <span>{row.label}</span>
                          <span className="text-meta text-ink-meta">{row.objectLabel}</span>
                        </span>
                      </td>
                      <td className="p-1 text-ink-600">{row.deletedByName ?? ''}</td>
                      <td className="p-1">
                        <Num className="text-ink-meta">
                          {formatRelativeDate(row.deletedAt, format)}
                        </Num>
                      </td>
                      <td className="p-1">
                        {/* A date, not a countdown - and the word when it has
                            already passed, because a colour alone reaches
                            nobody who cannot see it. */}
                        <Num
                          data-bin-expires={row.recordId}
                          className={row.expired ? 'text-warning-text' : 'text-ink-meta'}
                        >
                          {row.expired
                            ? t('bin.expired')
                            : formatDate(row.expiresAt, format)}
                        </Num>
                      </td>
                      <td className="p-1 text-end">
                        <span className="flex items-center justify-end gap-3">
                          <InlineAction
                            data-bin-restore={row.recordId}
                            onClick={() => restore.mutate(row)}
                          >
                            {t('bin.restore')}
                          </InlineAction>
                          <InlineAction
                            data-bin-purge={row.recordId}
                            onClick={() => setConfirming(row)}
                          >
                            {t('bin.purge')}
                          </InlineAction>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {restore.isError || purge.isError || purgeAll.isError ? (
          <p data-bin-refusal className="text-dense text-danger-text">
            {errorMessage(restore.error ?? purge.error ?? purgeAll.error!)}
          </p>
        ) : null}

        {confirming ? (
          <ConfirmDialog
            data-bin-confirm
            open
            title={t('bin.confirm.title', { values: { name: confirming.label } })}
            confirmLabel={t('bin.confirm.yes')}
            /* Danger, because this is the only control in the product that
               destroys something. */
            confirmIntent="danger"
            labels={{ cancel: t('action.cancel') }}
            busy={purge.isPending}
            onConfirm={() => purge.mutate(confirming)}
            onCancel={() => setConfirming(null)}
          >
            {/* What will happen, and what survives it. */}
            {t('bin.confirm.body')}
          </ConfirmDialog>
        ) : null}
      </div>
    </Panel>
  );
}
